/**
 * Phase 32 Plan 32-2 — per-engineer drill-down + self-view.
 *
 * Surface inventory (UI-SPEC §195-204):
 *   /reports/engineering/me                  ProtectedRoute → THIS PAGE (self-view)
 *   /reports/engineering/users/:userId       ManagerRoute → THIS PAGE (drill-down)
 *
 * Plan 32-3 anchor: an empty <section id="value-tag-anchor" /> is rendered
 * after the per-repo list. Plan 32-3 will mount the value-tag list and
 * "Add value tag" button into this slot — the route does NOT need to change
 * between phases.
 */
import { useParams, useSearchParams } from 'react-router-dom';
import { useState, type ReactElement } from 'react';

const numberFmt = new Intl.NumberFormat();
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../services/authContext';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { EngineerSparkline } from '../../components/engineering/EngineerSparkline';
import { ValueTagList } from '../../components/engineering/ValueTagList';
import ValueTagFormModal from '../../components/engineering/ValueTagFormModal';
import { useEngineerSeries } from '../../hooks/useEngineeringMetrics';
import { useProjects } from '../../hooks/useProjects';
import type { Window } from '../../services/api/engineeringMetrics';

const WINDOW_OPTIONS: Window[] = ['7d', '30d', '13w', 'all'];
const WINDOW_LABEL: Record<Window, string> = {
  '7d': '7 days',
  '30d': '30 days',
  '13w': '13 weeks',
  all: 'All time',
};

function isWindow(s: string | null): s is Window {
  return s === '7d' || s === '30d' || s === '13w' || s === 'all';
}

export default function EngineerDetailPage(): ReactElement {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user: currentUser, isAdmin, isManager } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tagModalOpen, setTagModalOpen] = useState(false);

  // FE flag is UX only — server enforces the full lead matrix (admin OR
  // PM-of-project OR ProjectAssignment.role contains 'lead').
  // v1 short-circuits to `isAdmin || isManager` because frontend has no
  // useProjectMembership lookup yet.
  // Future enhancement: replace with explicit useProjectMembership(projectId)
  // query so leads see the button.
  const canTag = isAdmin || isManager;

  // /me uses currentUser.id; /users/:userId parses the route param.
  const userIdFromParam = paramUserId ? Number(paramUserId) : undefined;
  const userId =
    userIdFromParam && Number.isFinite(userIdFromParam) ? userIdFromParam : currentUser?.id;
  const isSelf = !!currentUser && userId === currentUser.id;

  const windowKey: Window = isWindow(params.get('window')) ? (params.get('window') as Window) : '30d';
  const rawProjectId = params.get('projectId');
  const projectId =
    rawProjectId && /^\d+$/.test(rawProjectId) ? Number(rawProjectId) : undefined;

  const setWindow = (w: Window) => {
    params.set('window', w);
    setParams(params, { replace: true });
  };
  const setProjectId = (pid: number | undefined) => {
    if (pid === undefined) params.delete('projectId');
    else params.set('projectId', String(pid));
    setParams(params, { replace: true });
  };

  const projectsQ = useProjects();
  const seriesQ = useEngineerSeries(userId, { window: windowKey, projectId });

  const sparklineData =
    seriesQ.data?.perWeek?.map((value, week) => ({ week, value })) ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {isSelf
            ? 'My Engineering Activity'
            : seriesQ.data
              ? `Engineering Activity — ${seriesQ.data.userName}`
              : 'Engineering Activity'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {isSelf
            ? 'Your commit activity and AI-assisted share over the selected window.'
            : 'Per-engineer commit detail with a 13-week trend sparkline and per-repository breakdown.'}
          {seriesQ.data?.githubUsername && (
            <>
              {' '}
              <a
                href={`https://github.com/${encodeURIComponent(seriesQ.data.githubUsername)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-700 hover:text-primary-800 hover:underline"
                title={`Open @${seriesQ.data.githubUsername} on GitHub`}
              >
                @{seriesQ.data.githubUsername}
              </a>
            </>
          )}
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
          {WINDOW_OPTIONS.map((w) => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              aria-pressed={windowKey === w}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                windowKey === w
                  ? 'bg-white text-primary-700 shadow-sm font-semibold'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {WINDOW_LABEL[w]}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">Project:</span>
          <select
            value={projectId ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setProjectId(v ? Number(v) : undefined);
            }}
            className="rounded-md border-gray-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          >
            <option value="">All projects</option>
            {(projectsQ.data ?? [])
              .filter((p) => p.state === 'in_progress')
              .slice()
              .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </label>
      </div>

      {seriesQ.isLoading && (
        <div className="flex items-center justify-center h-32">
          <Spinner />
        </div>
      )}
      {seriesQ.error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load engineer series: {(seriesQ.error as Error).message}
        </div>
      )}

      {seriesQ.data && (
        <>
          {/* Window stats card */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase text-gray-500">Total commits</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">
                  {seriesQ.data.total}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-gray-500">AI-assisted share</div>
                <div className="mt-1 text-2xl font-semibold text-gray-900">
                  {Math.round(seriesQ.data.aiPct * 100)}%
                </div>
              </div>
              <div className="md:col-span-1 col-span-2">
                <div className="text-xs uppercase text-gray-500 mb-1">13-week trend</div>
                <EngineerSparkline data={sparklineData} isSelf={isSelf} />
              </div>
            </div>
            {/* Line-stats totals strip — quick-260513-92r */}
            <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-700">
              <div>
                <span className="text-green-700">+{numberFmt.format(seriesQ.data.additions.total)}</span>
                {' / '}
                <span className="text-red-700">&#x2212;{numberFmt.format(seriesQ.data.deletions.total)}</span>
                {' lines, '}
                {numberFmt.format(seriesQ.data.filesChanged.total)} files changed
              </div>
              <div className="mt-1 text-xs text-gray-500">
                ({numberFmt.format(seriesQ.data.additions.human + seriesQ.data.deletions.human)} human,{' '}
                {numberFmt.format(seriesQ.data.additions.aiAssisted + seriesQ.data.deletions.aiAssisted)} AI-assisted)
              </div>
            </div>
          </div>

          {/* Per-repo top 10 */}
          <div>
            <h2 className="text-sm font-semibold uppercase text-gray-400 mb-2">
              Top repositories ({WINDOW_LABEL[windowKey]})
            </h2>
            {seriesQ.data.perRepo.length === 0 ? (
              <p className="text-sm text-gray-500">
                No commits in this window. Squash-merge attribution may delay
                visibility for the most recent merge — see UI-SPEC §168.
              </p>
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Repository
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Commits
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                        AI-assisted %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {seriesQ.data.perRepo.map((r) => {
                      // r.repoName is `owner/name`. Build a GitHub URL filtered
                      // by author when we have a github login on the engineer's
                      // User row; otherwise link to the bare commits page.
                      const githubUrl = seriesQ.data?.githubUsername
                        ? `https://github.com/${r.repoName}/commits?author=${encodeURIComponent(seriesQ.data.githubUsername)}`
                        : `https://github.com/${r.repoName}/commits`;
                      return (
                      <tr key={r.repoId}>
                        <td className="px-4 py-2 text-sm font-mono text-gray-900">
                          <a
                            href={githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-700 hover:text-primary-800 hover:underline"
                            title={
                              seriesQ.data?.githubUsername
                                ? `View ${seriesQ.data.githubUsername}'s commits in ${r.repoName} on GitHub`
                                : `View ${r.repoName} commits on GitHub`
                            }
                          >
                            {r.repoName}
                          </a>
                        </td>
                        <td className="px-4 py-2 text-right text-sm text-gray-700">{r.total}</td>
                        <td className="px-4 py-2 text-right text-sm text-gray-700">
                          {Math.round(r.aiPct * 100)}%
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Plan 32-3 — value-tag list + "Add value tag" button mounted at the
              #value-tag-anchor slot reserved by Plan 32-2. */}
          <section id="value-tag-anchor" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Value tags</h2>
              {canTag && userId !== undefined && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setTagModalOpen(true)}
                >
                  <PlusIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                  Add value tag
                </Button>
              )}
            </div>
            {userId !== undefined && <ValueTagList userId={userId} />}
          </section>
          {tagModalOpen && userId !== undefined && (
            <ValueTagFormModal
              isOpen={tagModalOpen}
              onClose={() => setTagModalOpen(false)}
              defaultUserId={userId}
              defaultProjectId={projectId}
            />
          )}
        </>
      )}
    </div>
  );
}
